import React, { useState, useEffect, useMemo } from 'react';
import { CalendarIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { format as formatDateFns } from 'date-fns';
import { DatePickerInput } from '../DatePickerInput';

/**
 * Floor when `minDate` is omitted. When `maxDate` is omitted, the ceiling is Dec 31 of the current local year — see `maxYmd` in the component.
 */
const DEFAULT_MIN_YMD = '2022-01-01';

const DateRangePicker = ({
  startDate,
  endDate,
  onDateChange,
  label = "Date Range",
  error = null,
  disabled = false,
  maxDate = null,
  minDate = null,
  /**
   * date-fns pattern for each date inside the Start/End pickers (ISO value unchanged).
   * @example 'MM/dd/yyyy'
   */
  pickerDateFormat = 'MMM d, yyyy',
  /**
   * date-fns pattern for the collapsed range button (`start — end`). If omitted, uses `Date.prototype.toLocaleDateString()` (previous behavior).
   */
  summaryDateFormat = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate || '');
  const [tempEndDate, setTempEndDate] = useState(endDate || '');

  // Helpers to work with pure date (YYYY-MM-DD) without timezone shifts
  const pad2 = (n) => String(n).padStart(2, '0');

  // Create YYYY-MM-DD from a Date using LOCAL components
  const ymdFromLocalDate = (dateObj) => {
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return '';
    const y = dateObj.getFullYear();
    const m = pad2(dateObj.getMonth() + 1);
    const d = pad2(dateObj.getDate());
    return `${y}-${m}-${d}`;
  };

  // Normalize any accepted input (Date or string) to YYYY-MM-DD (no TZ)
  const toYMD = (value) => {
    if (!value) return '';
    if (value instanceof Date) return ymdFromLocalDate(value);
    if (typeof value === 'string') {
      // If already YYYY-MM-DD, trust as-is
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
      // Fallback: attempt to parse and then convert using LOCAL components
      const d = new Date(value);
      return ymdFromLocalDate(d);
    }
    return '';
  };

  const parseYMD = (ymd) => {
    if (typeof ymd !== 'string') return null;
    const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!y || mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    return { y, m: mo, d };
  };

  const compareYMD = (a, b) => {
    // returns negative if a < b, 0 if equal, positive if a > b
    if (!a || !b) return 0;
    const av = a.y * 10000 + a.m * 100 + a.d;
    const bv = b.y * 10000 + b.m * 100 + b.d;
    return av - bv;
  };

  const localDateFromYMD = (ymd) => {
    const p = parseYMD(ymd);
    if (!p) return null;
    return new Date(p.y, p.m - 1, p.d);
  };

  // Preset date ranges
  const presetRanges = [
    {
      label: 'Today',
      getValue: () => {
        const today = toYMD(new Date());
        return { startDate: today, endDate: today };
      }
    },
    {
      label: 'Yesterday',
      getValue: () => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        const yesterday = toYMD(d);
        return { startDate: yesterday, endDate: yesterday };
      }
    },
    {
      label: 'Last 7 days',
      getValue: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 7);
        return {
          startDate: toYMD(start),
          endDate: toYMD(end)
        };
      }
    },
    {
      label: 'Last 30 days',
      getValue: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 30);
        return {
          startDate: toYMD(start),
          endDate: toYMD(end)
        };
      }
    },
    {
      label: 'Last 90 days',
      getValue: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 90);
        return {
          startDate: toYMD(start),
          endDate: toYMD(end)
        };
      }
    },
    {
      label: 'This month',
      getValue: () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return {
          startDate: toYMD(start),
          endDate: toYMD(end)
        };
      }
    },
    {
      label: 'Last month',
      getValue: () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        return {
          startDate: toYMD(start),
          endDate: toYMD(end)
        };
      }
    },
    {
      label: 'This year',
      getValue: () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 1);
        const end = new Date(now.getFullYear(), 11, 31);
        return {
          startDate: toYMD(start),
          endDate: toYMD(end)
        };
      }
    }
  ];

  // Update temp values when props change
  useEffect(() => {
    setTempStartDate(startDate || '');
    setTempEndDate(endDate || '');
  }, [startDate, endDate]);

  const minYmd = useMemo(() => {
    if (minDate != null && minDate !== '') {
      const y = toYMD(minDate);
      if (y) return y;
    }
    return DEFAULT_MIN_YMD;
  }, [minDate]);

  const maxYmdResolvedFromProp = useMemo(() => {
    if (maxDate != null && maxDate !== '') {
      const y = toYMD(maxDate);
      if (y) return y;
    }
    return null;
  }, [maxDate]);

  const maxYmd =
    maxYmdResolvedFromProp ??
    ymdFromLocalDate(new Date(new Date().getFullYear(), 11, 31));

  // Validation function (using pure date semantics)
  const validateDateRange = (start, end) => {
    if (!start || !end) return null;
    const s = parseYMD(toYMD(start));
    const e = parseYMD(toYMD(end));
    if (!s || !e) return 'Invalid date';

    if (compareYMD(s, e) > 0) {
      return 'Start date must be before end date';
    }

    const md = parseYMD(minYmd);
    if (md && compareYMD(s, md) < 0) {
      const floorLabel = summaryDateFormat
        ? formatDateFns(localDateFromYMD(minYmd), summaryDateFormat)
        : localDateFromYMD(minYmd)?.toLocaleDateString();
      return `Start date cannot be before ${floorLabel}`;
    }

    const cap = parseYMD(maxYmd);
    if (cap) {
      const capLabel = summaryDateFormat
        ? formatDateFns(localDateFromYMD(maxYmd), summaryDateFormat)
        : localDateFromYMD(maxYmd)?.toLocaleDateString();
      if (compareYMD(s, cap) > 0) {
        return `Start date cannot be after ${capLabel}`;
      }
      if (compareYMD(e, cap) > 0) {
        return `End date cannot be after ${capLabel}`;
      }
    }

    return null;
  };

  const handleApply = () => {
    const validationError = validateDateRange(tempStartDate, tempEndDate);
    if (!validationError && onDateChange) {
      onDateChange({
        startDate: tempStartDate,
        endDate: tempEndDate
      });
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempStartDate(startDate || '');
    setTempEndDate(endDate || '');
    setIsOpen(false);
  };

  const handlePresetSelect = (preset) => {
    const { startDate: presetStart, endDate: presetEnd } = preset.getValue();
    setTempStartDate(presetStart);
    setTempEndDate(presetEnd);
    if (onDateChange) {
      onDateChange({
        startDate: presetStart,
        endDate: presetEnd
      });
    }
    setIsOpen(false);
  };

  const formatDisplayDate = (start, end) => {
    if (!start || !end) return 'Select date range';
    const s = localDateFromYMD(toYMD(start));
    const e = localDateFromYMD(toYMD(end));
    if (!s || !e) return 'Select date range';
    const startFormatted = summaryDateFormat
      ? formatDateFns(s, summaryDateFormat)
      : s.toLocaleDateString();
    const endFormatted = summaryDateFormat
      ? formatDateFns(e, summaryDateFormat)
      : e.toLocaleDateString();
    return `${startFormatted} - ${endFormatted}`;
  };

  const startPickerMax = useMemo(() => {
    let cap = maxYmd;
    if (tempEndDate) {
      const endNorm = toYMD(tempEndDate);
      if (endNorm) {
        cap = cap ? (endNorm < cap ? endNorm : cap) : endNorm;
      }
    }
    return cap || undefined;
  }, [maxYmd, tempEndDate]);

  const endPickerMin = useMemo(() => {
    let floor = minYmd;
    if (tempStartDate) {
      const startNorm = toYMD(tempStartDate);
      if (startNorm) {
        floor = floor ? (startNorm > floor ? startNorm : floor) : startNorm;
      }
    }
    return floor || undefined;
  }, [minYmd, tempStartDate]);

  const currentValidationError = validateDateRange(tempStartDate, tempEndDate);

  return (
    <div className="relative">
      {/* Display Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`relative w-full bg-white border rounded-md pl-3 pr-8 sm:pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:border-gray-500 text-sm ${
          disabled 
            ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed' 
            : error 
              ? 'border-red-300 focus:ring-red-500' 
              : 'border-gray-300 focus:ring-gray-500'
        }`}
      >
        <span className="flex items-center">
          <CalendarIcon className="h-5 w-5 text-gray-400 mr-1.5 sm:mr-2" />
          <span className="block truncate">
            {formatDisplayDate(startDate, endDate)}
          </span>
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
        </span>
      </button>

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-[35] mt-1 w-96 bg-white shadow-lg max-h-96 rounded-md border border-gray-200 overflow-hidden">
          <div className="flex">
            {/* Preset Ranges */}
            <div className="w-1/3 border-r border-gray-200 bg-gray-50">
              <div className="px-4 py-3 border-b border-gray-200">
                <h4 className="text-sm font-medium text-gray-900">Quick Select</h4>
              </div>
              <div className="py-2">
                {presetRanges.map((preset, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handlePresetSelect(preset)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Date Inputs */}
            <div className="flex-1 p-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <DatePickerInput
                    value={tempStartDate}
                    onChange={setTempStartDate}
                    placeholder="Start date"
                    min={minYmd}
                    max={startPickerMax}
                    disabled={disabled}
                    dateFormat={pickerDateFormat}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <DatePickerInput
                    value={tempEndDate}
                    onChange={setTempEndDate}
                    placeholder="End date"
                    min={endPickerMin}
                    max={maxYmd}
                    disabled={disabled}
                    dateFormat={pickerDateFormat}
                    className="w-full"
                  />
                </div>

                {/* Validation Error */}
                {currentValidationError && (
                  <p className="text-sm text-red-600">{currentValidationError}</p>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-2 pt-2">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApply}
                    disabled={!!currentValidationError}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${
                      currentValidationError
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-black hover:bg-gray-800'
                    }`}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker; 